/* ═══════════════════════════════════════════════════════
   Jobs — Insights (admin-only, revenue-only analytics)
   ═══════════════════════════════════════════════════════
   Company-level revenue analytics rebuilt from the retired
   nyc-suite InsightsPanel (docs/RETIREMENT-PARITY.md §5).
   Read-only over existing quotes + jobs — no new Firestore
   reads, collections, or context. REVENUE ONLY: no profit,
   margin, or cost figures (see insights.js for why).

   All derivation math lives in ../lib/insights.js. This file
   only reads that data and renders it. */

import { useState, useMemo } from "react";
import { useData } from "../../../context/DataContext.jsx";
import { C, font, lbl } from "../../../lib/styles.js";
import { fmt } from "../../../lib/utils.js";
import { INDUSTRY_LABELS } from "../../../lib/constants.js";
import {
  MONTH_NAMES,
  availableYears,
  revenueYoY,
  clientConcentration,
  quoteConversion,
  revenueByIndustry,
} from "../lib/insights.js";

/* ═══ VIEW-LEVEL HELPERS ═══ */
const fmtPct = (n, d) => (d === 0 ? "—" : Math.round((n / d) * 100) + "%");

// Year chip. Styles.js has no `chip` export (the old panel's did) — mirror the
// look here: filled when active, outlined when not.
const chipStyle = (active) => ({
  padding: "4px 11px",
  borderRadius: 6,
  fontSize: 12,
  fontFamily: font,
  fontWeight: 400,
  cursor: "pointer",
  background: active ? C.text : "transparent",
  color: active ? "#FFF" : C.muted,
  border: `1px solid ${active ? C.text : C.border}`,
});

const SectionHead = ({ title, sub }) => (
  <div style={{ marginBottom: 16 }}>
    <h3 style={{ fontSize: 14, fontWeight: 400, margin: 0 }}>{title}</h3>
    {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sub}</div>}
  </div>
);

const Stat = ({ label, value, sub, color }) => (
  <div style={{ minWidth: 100 }}>
    <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: C.muted, marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 400, color: color || C.text, fontFamily: font }}>{value}</div>
    {sub && <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>{sub}</div>}
  </div>
);

// Horizontal CSS-only bar (divs with width %). No chart library, exactly like the old panel.
const Bar = ({ value, max, color, label, amount }) => {
  const w = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
      <div style={{ width: 32, fontSize: 10, color: C.muted, textAlign: "right", flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, background: C.surfaceAlt, borderRadius: 3, height: 18, position: "relative", overflow: "hidden" }}>
        <div style={{ width: w + "%", height: "100%", background: color, borderRadius: 3, transition: "width 0.3s ease" }} />
      </div>
      <div style={{ width: 70, fontSize: 11, textAlign: "right", flexShrink: 0, fontFamily: font }}>{amount}</div>
    </div>
  );
};

const sectionStyle = {
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  padding: 20,
  marginBottom: 16,
};

export default function Insights() {
  const { jobs, savedQuotes, jobsLoading, quotesLoading } = useData();
  const allJobs = jobs || [];
  const allQuotes = savedQuotes || [];
  // The Jobs app re-fetches all collections on every entry, so this view routinely
  // mounts before data arrives. Gate on the loading flags — rendering the empty
  // arrays as "$0 / no data" flashes wrong money numbers until Firestore lands.
  const loading = jobsLoading || quotesLoading;

  const years = useMemo(() => availableYears(allJobs, allQuotes), [allJobs, allQuotes]);
  // Derived, not initial-state: data arrives after mount, so a useState(years[0])
  // would freeze on the pre-load fallback and never snap to the real newest year.
  const [pickedYear, setPickedYear] = useState(null);
  const selectedYear = pickedYear ?? years[0] ?? new Date().getFullYear();

  const revenue = useMemo(() => revenueYoY(allJobs, allQuotes, selectedYear), [allJobs, allQuotes, selectedYear]);
  const clients = useMemo(() => clientConcentration(allJobs, allQuotes, selectedYear), [allJobs, allQuotes, selectedYear]);
  const conversion = useMemo(() => quoteConversion(allJobs, allQuotes, selectedYear), [allJobs, allQuotes, selectedYear]);
  const industries = useMemo(() => revenueByIndustry(allJobs, allQuotes, selectedYear), [allJobs, allQuotes, selectedYear]);

  const noData = allJobs.length === 0 && allQuotes.length === 0;

  return (
    <div style={{ animation: "fadeUp 0.3s ease", fontFamily: font }}>
      {/* Header + Year Selector */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: 16, fontWeight: 400, margin: 0 }}>Insights</h2>
        {years.length > 0 && (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {years.map((y) => (
              <button key={y} style={chipStyle(y === selectedYear)} onClick={() => setPickedYear(y)}>{y}</button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: C.muted }}>
          Loading…
        </div>
      ) : noData ? (
        <div style={{ textAlign: "center", padding: 60, color: C.muted }}>
          No job or quote data yet. Insights will appear as you add quotes and book jobs.
        </div>
      ) : (
        <>
          {/* ═══ A) REVENUE YoY — TIERED AR ═══ */}
          <div style={sectionStyle}>
            <SectionHead title="Revenue" sub={`${revenue.thisYear} vs ${revenue.lastYear} — booked jobs incl. change orders`} />

            <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 20 }}>
              <Stat label={`${revenue.thisYear} Total AR`} value={fmt(revenue.totalCurrent)} />
              <Stat label="Paid" value={fmt(revenue.paid)} color={C.green} sub="collected" />
              <Stat label="Outstanding" value={fmt(revenue.outstanding)} color={C.amber} sub="invoiced, unpaid" />
              <Stat label="Projected" value={fmt(revenue.projected)} color={C.blue} sub="booked, not invoiced" />
              <Stat label={`${revenue.lastYear} Total AR`} value={fmt(revenue.totalPrevious)} color={C.muted} />
              {/* Pro bono (2026-09-11): value given away, never AR. Shown only once a
                  pro bono job exists in either year so the row stays quiet otherwise. */}
              {(revenue.proBonoCurrent > 0 || revenue.proBonoPrevious > 0) && (
                <Stat label="Pro bono value" value={fmt(revenue.proBonoCurrent)} color={C.muted}
                  sub={`${revenue.lastYear}: ${fmt(revenue.proBonoPrevious)} · not in AR`} />
              )}
            </div>

            {/* Monthly bars — current year (accent) with prior year (muted) underneath */}
            <div>
              {revenue.monthly.map((m, i) => (
                <div key={i} style={{ marginBottom: 6 }}>
                  <Bar value={m.current} max={revenue.maxMonth} color={C.blue} label={MONTH_NAMES[i]} amount={m.current > 0 ? fmt(m.current) : "—"} />
                  {m.previous > 0 && (
                    <Bar value={m.previous} max={revenue.maxMonth} color={`${C.muted}40`} label="" amount={fmt(m.previous)} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* ═══ B) CLIENT CONCENTRATION ═══ */}
          <div style={sectionStyle}>
            <SectionHead title="Client Concentration" sub={`${selectedYear} — ${clients.count} client${clients.count !== 1 ? "s" : ""}`} />

            <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 16 }}>
              <Stat label="Total AR" value={fmt(clients.total)} />
              <Stat label="Top Client Share" value={clients.topPct + "%"}
                color={clients.risk === "high" ? C.danger : clients.risk === "medium" ? C.amber : C.green} />
              <Stat label="Concentration"
                value={clients.risk === "high" ? "High Risk" : clients.risk === "medium" ? "Watch" : "Healthy"}
                color={clients.risk === "high" ? C.danger : clients.risk === "medium" ? C.amber : C.green} />
            </div>

            {clients.sorted.slice(0, 8).map((c, i) => (
              <Bar key={c.name} value={c.rev} max={clients.sorted[0]?.rev || 1}
                color={i === 0 && clients.risk === "high" ? C.danger : i === 0 && clients.risk === "medium" ? C.amber : C.blue}
                label={c.pct + "%"} amount={`${c.name}  ${fmt(c.rev)}`} />
            ))}
            {clients.sorted.length > 8 && (
              <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>+ {clients.sorted.length - 8} more</div>
            )}
          </div>

          {/* ═══ C) QUOTE CONVERSION ═══ */}
          <div style={sectionStyle}>
            <SectionHead title="Quote Conversion" sub={`${selectedYear} — ${conversion.total} quote${conversion.total !== 1 ? "s" : ""}`} />

            <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 16 }}>
              <Stat label="Win Rate" value={conversion.winRate + "%"} color={conversion.winRate >= 50 ? C.green : conversion.winRate >= 30 ? C.amber : C.danger} />
              <Stat label="Booked" value={conversion.booked} color={C.green} />
              <Stat label="Lost" value={conversion.lost} color={C.danger} />
              <Stat label="Open" value={conversion.open} color={C.amber} />
              <Stat label="Avg Won Value" value={conversion.avgWon > 0 ? fmt(conversion.avgWon) : "—"} />
              <Stat label="Avg Lost Value" value={conversion.avgLost > 0 ? fmt(conversion.avgLost) : "—"} />
              {conversion.avgDaysToBook != null && (
                <Stat label="Avg Days to Book" value={conversion.avgDaysToBook + "d"} />
              )}
            </div>

            {/* Conversion by industry */}
            {Object.keys(conversion.byIndustry).length > 0 && (
              <div>
                <div style={{ ...lbl, marginBottom: 8 }}>By Industry</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 8 }}>
                  {Object.entries(conversion.byIndustry)
                    .sort((a, b) => b[1].total - a[1].total)
                    .map(([ind, data]) => (
                      <div key={ind} style={{ background: C.surfaceAlt, borderRadius: 5, padding: "8px 10px" }}>
                        <div style={{ fontSize: 11, marginBottom: 2 }}>{INDUSTRY_LABELS[ind] || ind}</div>
                        <div style={{ fontSize: 10, color: C.muted }}>
                          {data.booked}/{data.total} booked ({fmtPct(data.booked, data.total)})
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* ═══ D) REVENUE BY INDUSTRY ═══ */}
          {industries.length > 0 && (
            <div style={sectionStyle}>
              <SectionHead title="Revenue by Industry" sub={String(selectedYear)} />
              {industries.map(({ ind, label, rev, pct }) => (
                <Bar key={ind} value={rev} max={industries[0]?.rev || 1} color={C.blue}
                  label={pct + "%"} amount={`${label}  ${fmt(rev)}`} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
